'use client'

import clsx from 'clsx'
import { CSSProperties, ReactNode, useLayoutEffect, useRef, useState } from 'react'

import { Button } from '@/shared/ui/Button'

import styles from './expandable-items.module.scss'

const DEFAULT_VISIBLE_ROWS = 1

type ExpandableItemsProps = {
	children: ReactNode
	visibleRows?: {
		mobile?: number
		desktop?: number
	}
	className?: string
	toggleClassName?: string
	expandLabel?: string
	collapseLabel?: string
	showMobileScrollBar?: boolean
}

export const ExpandableItems = ({
	children,
	visibleRows,
	className,
	toggleClassName,
	expandLabel = `Показать еще`,
	collapseLabel = `Спрятать`,
	showMobileScrollBar = false
}: ExpandableItemsProps): JSX.Element => {
	const mobileRows = visibleRows?.mobile ?? DEFAULT_VISIBLE_ROWS
	const desktopRows = visibleRows?.desktop ?? DEFAULT_VISIBLE_ROWS
	const [expanded, setExpanded] = useState(false)
	const [hasOverflow, setHasOverflow] = useState(false)
	const listRef = useRef<HTMLDivElement>(null)

	useLayoutEffect(() => {
		const node = listRef.current
		if (!node || expanded) return

		const update = () => {
			setHasOverflow(node.scrollHeight > node.clientHeight + 1)
		}

		update()
		const resizeObserver = new ResizeObserver(update)
		resizeObserver.observe(node)
		const mutationObserver = new MutationObserver(update)
		mutationObserver.observe(node, { childList: true, subtree: true })

		return () => {
			resizeObserver.disconnect()
			mutationObserver.disconnect()
		}
	}, [expanded, mobileRows, desktopRows])

	return (
		<div
			className={clsx(
				styles.wrap,
				className,
				hasOverflow && styles.hasOverflow,
				expanded && styles.expanded,
				showMobileScrollBar && styles.showMobileScrollBar
			)}
			style={
				{
					'--visible-rows-mobile': mobileRows,
					'--visible-rows-desktop': desktopRows
				} as CSSProperties
			}>
			<div className={styles.list} ref={listRef}>
				{children}
			</div>
			{hasOverflow && (
				<Button
					theme="link"
					type="button"
					aria-expanded={expanded}
					onClick={() => setExpanded(prev => !prev)}
					className={clsx(styles.toggle, toggleClassName)}>
					{expanded ? collapseLabel : expandLabel}
				</Button>
			)}
		</div>
	)
}
